package com.crowpay.views.components.nav

import com.crowpay.*
import com.crowpay.notifications.*
import com.crowpay.sdk.currentSession
import com.crowpay.utils.*
import com.crowpay.views.AutoRoutes
import com.crowpay.views.components.unreadIconWhen
import com.crowpay.views.theming.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.AppScope
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.UrlLikePath
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.children
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.*
import kotlinx.coroutines.launch
import kotlin.collections.any

private fun <T> List<T>.safeSlice(range: IntRange) = slice(range.first..range.last.coerceAtMost(size - 1))

object UserNotifications : Page {
    override val title: Readable<String> = Constant("Alerts")

    val category = Property<AlertCategory?>(null)

    val notifications = shared {
        currentSession()?.let { session ->
            session.notifications.watch(
                Query(
                    condition { notif -> notif.user eq session.userId },
                    orderBy = sort {
                        it.content.read.ascending()
                        it.sendAt.descending()
                    },
                    limit = 50
                )
            )
        } ?: ConstantLimitReadable(emptyList())
    }

    override fun ViewWriter.render2(): ViewModifiable {
        return col {
            scrollsHorizontally - row {
                onlyWhen { !windowIsNarrow() } - centered - subTitle("Alerts")
                for (cat in listOf(null) + AlertCategory.entries) {
                    centered - buttonTheme - compact - radioToggleButton {
                        checked bind category.equalTo(cat)
                        specCenteredContent {
                            row {
                                centered - unreadIconWhen {
                                    notifications()().filter { it.content.category == cat }.any { !it.content.read }
                                }
                                body(cat?.displayName ?: "All")
                            }
                        }
                    }
                }
            }
            greySeparator()
            expanding - recyclerView {
                ::exists { notifications()().isNotEmpty() }

                reactive {
                    val limiter = notifications()
                    if (lastIndex() >= limiter.limit - 10) {
                        limiter.limit += 25
                    }
                }

                val nowRead = HashSet<UUID>()

                onRemove {
                    if (nowRead.isEmpty()) return@onRemove
                    AppScope.launch {
                        currentSession()?.notifications?.bulkModify(
                            MassModification(
                                condition { it._id inside nowRead },
                                modification { it.content.read assign true }
                            )
                        )
                    }
                }

                reactive {
                    notifications()()
                        .takeUnless { it.isEmpty() }
                        ?.safeSlice(firstIndex()..lastIndex())
                        ?.filter { !it.content.read }
                        ?.map { it._id }
                        ?.let(nowRead::addAll)
                }

                children(
                    items = shared {
                        notifications()().let { notifs ->
                            category()?.let { cat -> notifs.filter { it.content.category == cat } } ?: notifs
                        }
                    },
                    id = { it._id },
                ) { notification ->
                    compact - link {
                        row {
                            icon {
                                ::source {
                                    when (notification().content.category) {
                                        AlertCategory.Info -> Icon.info
                                        AlertCategory.ActionNeeded -> Icon.activityInverted
                                        AlertCategory.CriticalActionNeeded -> Icon.activityInverted
                                    }
                                }
                                dynamicTheme {
                                    val color = when (notification().content.category) {
                                        AlertCategory.Info -> AppColors.blue.light1
                                        AlertCategory.ActionNeeded -> AppColors.yellow.main
                                        AlertCategory.CriticalActionNeeded -> AppColors.red.main
                                    }
                                    ForegroundSemantic(color)
                                }
                            }
                            expanding - compact - col {
                                smallBody {
                                    ::content { notification().event.time.format(Formats.mmddyyyy_hmm) }
                                }
                                bold - row {
                                    centered - unreadIconWhen { !notification().content.read }
                                    subTitle {
                                        ::content { notification().content.title }
                                    }
                                }
                                gravity(Align.Stretch, Align.Stretch) - body {
                                    ::content { notification().content.body }
                                }
                            }
                        }

                        onNavigate { closePopovers() }

                        ::to {
                            notification()
                                .content.url
//                                    ?.also(::println)
                                ?.substringAfter("://")
//                                    ?.also(::println)
                                ?.substringAfter('/')
//                                    ?.also(::println)
                                ?.let(UrlLikePath.Companion::fromUrlString)
//                                    ?.also { println("Parsed URL: $it") }
                                ?.let(AutoRoutes::parse)
//                                    ?.also { println("Got screen ${it.title.once()}") }
                                ?.let { { it } }
                        }
                    }
                }
            }
            expanding - stack {
                existsDefaultFalse { notifications()().isEmpty() }
                centered - colored(AppColors.grey.main) - subTitle("Nothing Yet...")
            }
        }
    }

    fun navButton(writer: ViewWriter, setup: RView.() -> Unit = {}) = with (writer) {
        menuButton {
            setup()
            requireClick = true
            preferredDirection = PopoverPreferredDirection.belowCenter

            colored(AppColors.primary.main) - icon(Icon.notificationBell, "notifications")

            atTopEnd - sizeConstraints(minHeight = 1.rem, aspectRatio = 1.0) - (Unread + CardSemantic).onNext - text {
                align = Align.Center
                ::content {
                    notifications()().count { !it.content.read }.takeUnless { it > 9 }?.toString() ?: "9+"
                }
                existsDefaultFalse { notifications()().any { !it.content.read } }
            }

            opensMenu {
                sizeConstraints(height = 40.rem) - render2()
            }
        }
    }
}